import { post, get, axiosApi, patch } from '../helpers/api.helper';
import config from '../config/config';
import { ServiceResponseType, parseServiceResponse } from '@/helpers/serviceParsing';
import { useAnonymousUserStore, useUserStore } from '@/store/userStore';
import { useVendorAuthStore } from '@/store/vendorAuthStore';
import { createAccountByToken, getMallUserIdByToken } from './mallUser.service';
import { useAnonymousCartStore } from '@/store/anonymousCartStore';
import { useOrderNPayStore } from '@/store/orderNPayStore';
import router from '@/router';

const { user_api, common_api, api_key, api_secret, mall_api, studio_api, matching_api } = config.api;

export async function loginWithEmail(email: string, password: string) {
    try {
        const responseSecret = await getUserSecretWithEmail(email, password);

        const token = await getToken(responseSecret?.user_id, responseSecret?.user_secret);
        axiosApi.defaults.headers.common['Authorization'] = `Bearer ${token?.access_token}`;
        useUserStore.setState({
            accessToken: token?.access_token,
            expiresIn: token?.expires_in,
        });
        const isMallUser = await checkIsMallUser(token.access_token).then((r) => r.data);
        if (isMallUser) {
            const { mallUserId } = await getMallUserIdByToken(token.access_token);
            useUserStore.setState({
                mallUserId,
            });
        } else {
            const { mallUserId } = await createAccountByToken(token.access_token);
            useUserStore.setState({
                mallUserId,
            });
        }
        const { reset } = useAnonymousUserStore.getState();
        reset();
    } catch (error) {
        throw error;
    }
}

export async function socialLogin(social: string, code: string, username?: string) {
    try {
        const body = social === 'apple' ? { code, username } : { code };
        const { userId, userSecret } = await post(`${user_api}/login/${social}`, { ...body }).then((r) => r.data);
        const token = await getToken(userId, userSecret);
        axiosApi.defaults.headers.common['Authorization'] = `Bearer ${token?.access_token}`;
        useUserStore.setState({
            accessToken: token?.access_token,
            expiresIn: token?.expires_in,
        });
        const isMallUser = await checkIsMallUser(token.access_token).then((r) => r.data);
        if (isMallUser) {
            const { mallUserId } = await getMallUserIdByToken(token.access_token);
            useUserStore.setState({
                mallUserId,
            });
        } else {
            const { mallUserId } = await createAccountByToken(token.access_token);
            useUserStore.setState({
                mallUserId,
            });
        }
        const { reset } = useAnonymousUserStore.getState();
        reset();
    } catch (err) {
        throw parseServiceResponse(err);
    }
}

// 토큰으로 로그인하는 함수
export async function loginWithToken(token: string) {
    try {
        // 전달받은 토큰을 Authorization 헤더에 설정
        axiosApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // userStore에 토큰 정보 저장
        useUserStore.setState({
            accessToken: token,
            expiresIn: '',
        });

        // 저장 후 즉시 확인
        const isMallUser = await checkIsMallUser(token).then((r) => r.data);

        if (isMallUser) {
            const { mallUserId } = await getMallUserIdByToken(token);
            useUserStore.setState({
                mallUserId,
            });
        } else {
            const { mallUserId } = await createAccountByToken(token);
            useUserStore.setState({
                mallUserId,
            });
        }

        const { reset } = useAnonymousUserStore.getState();
        reset();
    } catch (error) {
        throw error;
    }
}

// 로그인 상태 확인 함수
export function isLogined(): boolean {
    // 소매 쇼핑몰: userStore 확인
    const { accessToken: userAccessToken } = useUserStore.getState();
    // 도매 쇼핑몰: vendorAuthStore 확인
    const { accessToken: vendorAccessToken } = useVendorAuthStore.getState();

    return !!userAccessToken || !!vendorAccessToken;
}

export async function logOut() {
    const { reset: anonymousReset } = useAnonymousUserStore.getState();
    const { reset: userReset } = useUserStore.getState();
    const { reset: payStoreReset } = useOrderNPayStore.getState();
    try {
        const response = await post(`${common_api}/auth/web/sign-out`);
        // window.location.href = `${window.location.origin}/mall/${window.location.pathname.split('/')[2]}`
        await getTokenByAnonymous();
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    } finally {
        // axiosApi.defaults.headers.common['Authorization'] = undefined;
        // sessionStorage.clear();
        localStorage.clear();
        useUserStore.persist.clearStorage();
        userReset();
        // useOrderNPayStore.persist.clearStorage()
        // payStoreReset()
    }
}

export async function getUserInfo(userId: string): GetUserInfoResponseType {
    try {
        const response = await post(`${user_api}/profile/find`, { user_id: userId });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function checkNameDuplicate(name: string) {
    try {
        let response = await get(`${user_api}/nickname/duplicate?nickname=${name}`);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getToken(user_id: string, userSecret: string): GetTokenResponseType {
    try {
        const token = await post(`${common_api}/auth/web/getToken`, { user_id, user_secret: userSecret });
        return parseServiceResponse(token);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getTokenByAnonymous(): GetTokenResponseType {
    try {
        const token = await post(`${common_api}/auth/web/getToken`, {
            api_key,
            api_secret,
        });
        useAnonymousUserStore.setState({
            accessToken: token.data.access_token,
            expiresIn: token.data.expires_in,
        });
        return parseServiceResponse(token);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getRefreshTokenByAnonymous(): GetTokenResponseType {
    try {
        const token = await post(`${common_api}/auth/web/refreshToken`, {
            api_key,
            api_secret,
        });
        return parseServiceResponse(token);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function checkRefreshToken2Validation() {
    try {
        return (
            await post(`${common_api}/auth/web/refreshToken/check`, {
                api_key,
                api_secret,
            })
        ).data as boolean;
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getFlowingUserIdByToken() {
    try {
        const response = await post(`${common_api}/auth/web/user`, {});
        return parseServiceResponse<{ user_id: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function setHeaderAuthorization(accessToken: string) {
    axiosApi.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
}

export async function clearHeaderAuthorization() {
    axiosApi.defaults.headers.common.Authorization = '';
}

export async function getUserSecretWithEmail(email: string, password: string): GetUserSecretWithEmailResponseType {
    try {
        const response = await post(`${user_api}/sign-in/formal`, { email, password });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestPasswordResetEmail(email: string) {
    try {
        const response = await post(`${user_api}/password/email/renew/request`, { email });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function changeVendorPassword(vendorId: string, currentPassword: string, newPassword: string) {
    try {
        const response = await post(`${matching_api}/vendor/${vendorId}/change-password`, {
            currentPassword,
            newPassword,
        });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestVendorPasswordResetEmail(email: string) {
    try {
        const response = await post(`${matching_api}/vendor/password/email/renew/request`, { email });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

// 👇 새롭게 추가된 회원가입 관련 API
export async function requestEmailAuthCode(body: { email: string }) {
    try {
        const request = await post(`${user_api}/email/pre-signup/request`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function certifyEmailAuthCode(body: { email: string; auth_code: string }) {
    try {
        const request = await post(`${user_api}/email/pre-signup/authenticate`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestActivateAuthCode(userId: string, body: { email: string }) {
    try {
        const request = await post(`${user_api}/${userId}/email/activation/request`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function certifyActivateAuthCode(userId: string, body: { email: string; auth_code: string }) {
    try {
        const request = await post(`${user_api}/${userId}/email/activation/authenticate`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function signIn(body: { email: string; password: string }) {
    try {
        const { user_id, user_secret } = await post(`${user_api}/sign-in`, { ...body }).then((r) => {
            if (r.status !== 200) throw r;
            return r.data;
        });
        const token = await getToken(user_id, user_secret);
        axiosApi.defaults.headers.common['Authorization'] = `Bearer ${token?.access_token}`;
        useUserStore.setState({
            accessToken: token?.access_token,
            expiresIn: token?.expires_in,
        });
        const { mallUserId } = await createAccountByToken(token.access_token);
        useUserStore.setState({
            mallUserId,
        });
        const { reset } = useAnonymousUserStore.getState();
        reset();
    } catch (error) {
        throw error;
    }
}

export async function signUp(body: SignUpReqBodyType) {
    try {
        const request = await post(`${user_api}/sign-up`, { ...body });
        return parseServiceResponse<{ user_id: string; user_secret: string }>(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function resetPassword(userId: string, body: { current_password: string; password: string }) {
    try {
        const request = await patch(`${user_api}/${userId}/reset/password`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function addUserEmail(userId: string, reqBody: { email: string; password: string }) {
    try {
        const request = await patch(`${user_api}/${userId}/email`, { ...reqBody });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

// checkSession Logic
export async function checkRefreshToken() {
    try {
        const request = await post(`${common_api}/auth/web/refreshToken`).then((r) => {
            if (r.status !== 201) throw r;
            return r;
        });
        return parseServiceResponse<CheckRefreshTokenType>(request);
    } catch (error) {
        throw error;
    }
}
export async function checkRefreshTokenBool() {
    try {
        const request = await post(`${common_api}/auth/web/refreshToken/check`).then((r) => {
            if (r.status !== 201) throw r;
            return r;
        });
        return request;
    } catch (error) {
        throw error;
    }
}

export async function checkIsMallUser(access_token: string) {
    try {
        const request = await post(
            `${mall_api}/main/mall-user-id/permission`,
            {},
            { headers: { Authorization: `Bearer ${access_token}` } },
        ).then((r) => {
            if (r.status !== 200) throw r;
            return r;
        });
        return request;
    } catch (error) {
        throw error;
    }
}

interface SourcingReferrerQueryType {
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
    utm_referrer: string;
    utm_campaign_id: string;
    user_id: string;
    referer: string;
    utm_content: string;
    utm_term: string;
}

export async function setReferrerToken(query: SourcingReferrerQueryType) {
    try {
        const utm_source = `utm_source=${query?.utm_source || ''}`;
        const utm_medium = `utm_medium=${query?.utm_medium || ''}`;
        const utm_campaign = `utm_campaign=${query?.utm_campaign || ''}`;
        const utm_referrer = `utm_referrer=${query?.utm_referrer || ''}`;
        const utm_campaign_id = `utm_campaign_id=${query?.utm_campaign_id || ''}`;
        const user_id = `user_id=${query?.user_id || ''}`;
        const referer = `referer=${query?.referer || ''}`;
        const utm_content = `utm_content=${query?.utm_content || ''}`;
        const utm_term = `utm_term=${query?.utm_term || ''}`;

        const queryStr = `${utm_source}&${utm_medium}&${utm_campaign}&${utm_referrer}&${utm_campaign_id}&${user_id}&${referer}&${utm_content}&${utm_term}`;
        return await post(`${common_api}/auth/web/referrerToken?${queryStr}`)
            .then((r) => r?.data?.referrer_token || '')
            .catch(() => '');
    } catch (error) {
        throw error;
    }
}

export async function setReferrerRefreshToken(query: SourcingReferrerQueryType) {
    try {
        const utm_source = `utm_source=${query?.utm_source || ''}`;
        const utm_medium = `utm_medium=${query?.utm_medium || ''}`;
        const utm_campaign = `utm_campaign=${query?.utm_campaign || ''}`;
        const utm_referrer = `utm_referrer=${query?.utm_referrer || ''}`;
        const utm_campaign_id = `utm_campaign_id=${query?.utm_campaign_id || ''}`;
        const user_id = `user_id=${query?.user_id || ''}`;
        const referer = `referer=${query?.referer || ''}`;
        const utm_content = `utm_content=${query?.utm_content || ''}`;
        const utm_term = `utm_term=${query?.utm_term || ''}`;
        const queryStr = `${utm_source}&${utm_medium}&${utm_campaign}&${utm_referrer}&${utm_campaign_id}&${user_id}&${referer}&${utm_content}&${utm_term}`;
        return await post(`${common_api}/auth/web/refresh-referrerToken?${queryStr}`)
            .then((r) => r?.data?.referrer_token || '')
            .catch(() => '');
    } catch (error) {
        throw error;
    }
}

type CheckRefreshTokenType = {
    access_token: string;
    expires_in: string;
};

/*👇타입 */
type GetUserInfoResponseType = Promise<
    {
        id: string;
        phone_number?: string;
        email: string;
        image_url: string;
        nickname: string;
        gender: string;
        age_group: string;
        invitation_code: string;
        accessToken: string;
    } & ServiceResponseType
>;

type GetTokenResponseType = Promise<
    {
        access_token: string;
        expires_in: string;
    } & ServiceResponseType
>;

type GetUserSecretWithEmailResponseType = Promise<
    {
        user_id: string;
        user_secret: string;
    } & ServiceResponseType
>;

export type SignUpReqBodyType = {
    nickname: string;
    email: string;
    password: string;
    phone_number?: string;
    age_group?: string;
    gender?: string;
    image_url?: string;
    invitation_user_code?: string;
};

interface ProfileLinkSignUpWithEmailProp {
    nickname: string;
    email: string;
    phoneNumber: string;
    password: string;
    imageUrl?: string;
    smsMarketingConsented?: boolean;
    emailMarketingConsented?: boolean;
}

export async function mallSignUpV2(body: ProfileLinkSignUpWithEmailProp) {
    // 회원가입 로직 단순화 위해 스튜디오 회원가입과 동일한 API 사용
    try {
        return await post(`${studio_api}/users/sign-up`, { ...body });
    } catch (error) {
        throw error;
    }
}

// Vendor 로그인 관련 함수들
export async function vendorSignIn(body: { email: string; password: string }) {
    try {
        // 1단계: vendor sign-in으로 vendorId와 vendorSecret 획득
        const { vendorId, vendorSecret } = await post(`${matching_api}/vendor/sign-in`, { ...body }).then((r) => {
            if (r.status !== 200) throw r;
            return r.data.item;
        });

        // 2단계: vendor token 획득
        const token = await getVendorToken(vendorId, vendorSecret);

        // 3단계: axios 헤더에 토큰 설정
        axiosApi.defaults.headers.common['Authorization'] = `Bearer ${token?.access_token}`;

        // 4단계: vendorAuthStore에 토큰 정보 저장 (vendorSecret은 저장하지 않음)
        useVendorAuthStore.setState({
            accessToken: token?.access_token,
            expiresIn: token?.expires_in,
            vendorId: vendorId,
        });

        // 5단계: vendor 정보 조회 및 저장
        const vendorInfoResponse = await getVendorInfo(vendorId);
        useVendorAuthStore.setState({
            vendorInfo: vendorInfoResponse.item,
        });

        // vendorSecret은 보안상 클라이언트에 저장하지 않음
        // 메모리에서 즉시 제거됨

        const { reset } = useAnonymousUserStore.getState();
        reset();
    } catch (error) {
        throw error;
    }
}

export async function getVendorToken(vendorId: string, vendorSecret: string): GetTokenResponseType {
    try {
        const token = await post(`${common_api}/auth/vendor/web/getToken`, {
            user_id: vendorId,
            user_secret: vendorSecret,
        });
        return parseServiceResponse(token);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getVendorInfo(vendorId: string) {
    try {
        const response = await get(`${matching_api}/vendor/${vendorId}`);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function updateVendorProfile(vendorId: string, data: {
    nickname?: string;
    contactInfo?: {
        managerName?: string;
        managerPhoneNumber?: string;
        customerCenterNumber?: string;
    };
}) {
    try {
        const response = await patch(`${matching_api}/vendor/${vendorId}`, data);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function vendorLogOut() {
    // vendorAuthStore만 초기화 (shopStore의 쇼핑몰 정보는 유지)
    try {
        // vendor 로그아웃 API 호출 (필요한 경우)
        // await post(`${common_api}/auth/vendor/web/sign-out`);
        await getTokenByAnonymous();
        return { success: true };
    } catch (error) {
        throw parseServiceResponse(error);
    } finally {
        // 인증 정보만 초기화 (shopStore는 별도 스토어이므로 자동으로 유지됨)
        useVendorAuthStore.getState().reset();
    }
}

// Vendor 이메일 인증 요청
export async function requestVendorEmailAuthCode(body: { email: string }) {
    try {
        const request = await post(`${matching_api}/vendor/email/pre-signup/request`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

// Vendor 이메일 인증 확인
export async function certifyVendorEmailAuthCode(body: { email: string; authCode: string }) {
    try {
        const request = await post(`${matching_api}/vendor/email/pre-signup/authenticate`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

// Vendor 닉네임 중복 확인
export async function checkVendorNickname(nickname: string) {
    try {
        const response = await post(`${matching_api}/vendor/nickname/check`, { nickname });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

// Vendor 회원가입 (기존 app_user 회원가입과 동일한 정보 수집)
export interface VendorSignUpBody {
    nickname: string;
    email: string;
    password: string;
    profileImage?: string;
    smsMarketingConsented?: boolean;
    emailMarketingConsented?: boolean;
    contactInfo: {
        managerName: string;
        managerPhoneNumber: string;
        customerCenterNumber?: string;
    };
    businessRegistrationNumber?: string;
    companyName?: string;
    ownerName?: string;
    companyAddress?: string;
    businessRegistrationCertificateUrl?: string;
}

export async function vendorSignUp(body: VendorSignUpBody) {
    try {
        const response = await post(`${matching_api}/vendor/sign-up/shop3`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
